"use client";

import { Button } from "@/components/atoms/Button";
import Learners from "@/components/molecules/Learners";
import Carousel1 from "@/components/organisms/carousels/Carousel1";
import {
  carouselClick,
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { preload } from "react-dom";
import { bannerPreloads } from "./bannerPreload";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS, PAGE_TYPES } from "../../../constants";
import { Item } from "@radix-ui/react-accordion";

export const BANNER1_FONT_FAMILY_MAP = {
  section: "var(--font-poppins)",
  preTitle: "var(--font-poppins)",
  primaryHeading: "var(--font-poppins)",
  secondaryHeading: "var(--font-poppins)",
  body: "var(--font-poppins)",
  cta: "var(--font-poppins)",
  learners: "var(--font-poppins)",
  rating: "var(--font-poppins)",
} as const;

export type Banner1FontFamilyMap = typeof BANNER1_FONT_FAMILY_MAP;

interface BannerItem {
  id: number;
  pre_title: string;
  title_1: string;
  title_2: string;
  cta_name: string;
  priority: number;
  status: boolean;
  mobile_bg_image: string;
  desktop_bg_image: string;
  alt_image: string;
  university_tag_text_color?: string;
  university_tag_background_color?: string;
  heading_text_color?: string;
  button_color?: string;
  button_text?: string;
  button_text_color?: string;
  button_icon?: string;
  button_icon_alt?: string;
  gradient_angle?: string;
  gradient_start_color?: string;
  gradient_end_color?: string;
  mobile_gradient_angle?: string;
  mobile_gradient_start_color?: string;
  mobile_gradient_end_color?: string;
}

/** Aligns with `--breakpoint-sm`; only one banner branch mounts so h1/h2 are not duplicated in the DOM. */
const BANNER1_DESKTOP_MIN_PX = 1080;

/**
 * Overlay gradients for one slide, as CSS custom properties.
 *
 * Desktop and mobile are configured independently in the CMS (`gradient_*` vs
 * `mobile_gradient_*` — see BANNER_1_CONSTANTS.BANNER_FIELDS_CONFIG), but an inline
 * `background` cannot be media-queried, so both are handed to the element as vars and
 * the class list picks one per breakpoint. Defaults mirror
 * `BANNER_1_CONSTANTS.DEFAULT_ROW`, so an unconfigured slide renders exactly as before.
 *
 * Desktop keeps its hardcoded -25.81% / 78.26% stops (tuned for the 254deg diagonal).
 * Mobile spans the full box, which is what the flat-scrim default (identical start and
 * end colour) intends and what a custom two-colour mobile gradient wants too.
 */
const bannerOverlayGradients = (item: BannerItem) =>
  ({
    "--banner1-overlay-mobile": `linear-gradient(${
      item?.mobile_gradient_angle || "0deg"
    }, ${item?.mobile_gradient_start_color || "rgba(5, 33, 50, 0.80)"}, ${
      item?.mobile_gradient_end_color || "rgba(5, 33, 50, 0.80)"
    })`,
    "--banner1-overlay-desktop": `linear-gradient(${
      item?.gradient_angle || "254deg"
    }, ${item?.gradient_start_color || "rgba(0, 0, 0, 0.00)"} -25.81%, ${
      item?.gradient_end_color || "rgba(10, 61, 91, 1)"
    } 78.26%)`,
  }) as CSSProperties;

/** Matches `blogArticleColumnClass` in NewPage (blog band article / left column width). */
const BLOG_BANNER_TITLE_MAX_WIDTH_CLASS =
  "w-full max-w-full sm:max-w-[60%] md:max-w-[60%] lg:max-w-[60%] xl:max-w-[70%] leading-tight";

const Banner1 = (props: any) => {
  const { data: sectionData, pageCategory } = props;

  const dispatch = useDispatch();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const bannerRef = useRef<HTMLElement>(null);

  const isBlogPage =
    String(pageCategory ?? "").toLowerCase() === PAGE_TYPES.BLOG.toLowerCase();

  // Extract data early, before early returns
  const {
    metadata = [],
    metadata_1 = [],
    learners_description = "",
    learners_description_color = "#000000",
    slide_frequency = 5000,
    learners_status,
    learners_description_section_color,
    bleed_background_color,
  } = sectionData || {};

  // Full-bleed banner background colour shown in the desktop side gutters; defaults to white when unset.
  const bannerBackgroundColor = bleed_background_color || "#FFFFFF";

  // Filter active items and sort by priority - MUST be called before early returns
  const activeBannerItems: BannerItem[] = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];

    return metadata
      .filter((item: BannerItem) => item.status)
      .sort((a: BannerItem, b: BannerItem) => a.priority - b.priority);
  }, [metadata]);

  // Sort learners metadata by priority
  const sortedLearnersMetadata = useMemo(() => {
    if (!metadata_1 || metadata_1.length === 0) return [];

    return [...metadata_1]
      .filter((item: any) => item.status === undefined || item.status)
      .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0));
  }, [metadata_1]);

  // Track banner slide viewed when slide changes
  useEffect(() => {
    if (
      activeBannerItems.length > 0 &&
      currentSlideIndex < activeBannerItems.length
    ) {
      const currentItem = activeBannerItems[currentSlideIndex];
      carouselClick({
        section: "top_banner",
        label:
          currentItem.title_1 ||
          currentItem.title_2 ||
          `banner_slide_${currentSlideIndex + 1}`,
      });
    }
  }, [currentSlideIndex, activeBannerItems]);

  // Track CTA button click handler
  const handleCtaClick = (item: BannerItem, index: number) => {
    ctaClick({
      section: "top_banner",
      component: "banner_cta",
      label: item.button_text || item.cta_name || "apply_now",
      step: "1",
      entity: "banner",
      parentEntity: `banner_slide_${index + 1}`,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  };

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (bannerRef.current) {
  //     FireWidgetViewedEvent(bannerRef, "top_banner");
  //   }
  // }, []);

  if (activeBannerItems.length === 0) {
    return (
      <section className="w-full p-8 bg-gray-100 rounded-lg">
        <p className="text-gray-500 text-center">No banner items to display</p>
      </section>
    );
  }

  return (
    <section
      ref={bannerRef}
      className={cn(
        "w-full",
        // Desktop: the full-bleed section carries the CMS background colour, filling the
        // sm:px-8 side gutters around the inset carousel card. Mobile is untouched.
        "relative sm:px-8 sm:bg-(--banner1-bg)",
        isBlogPage ? "sm:h-95 sm:min-h-95" : " sm:aspect-1012/378", // desktop specific
      )}
      style={{ "--banner1-bg": bannerBackgroundColor } as CSSProperties}
    >
      <Carousel1
       autoplayDelay={slide_frequency}
          options={{ loop: true, duration: 45 }}
          navigationButtonClassName="absolute bottom-8 left-1/2 -translate-x-1/2"
          onSlideChange={(index) => setCurrentSlideIndex(index)}
      >
        {activeBannerItems.map((item, index) => {
          // Only the first slide: the rest are already loading="lazy".
          // The decision of what to preload, and at which widths, lives in bannerPreload.ts
          // so the media queries can be pinned by tests against the <source> queries below.
          if (index === 0) {
            for (const p of bannerPreloads(item, BANNER1_DESKTOP_MIN_PX)) {
              preload(p.href, { as: "image", fetchPriority: "high", media: p.media });
            }
          }

          return (
            <div
              key={item.id || index}
              className={cn(
                "min-w-full flex-[0_0_100%] sm:pb-0 sm:px-0",
                // Only reserve bottom space for the carousel dots when there is more than one slide.
                // A single slide renders no dots, so pb-16 would just expose the gray page background.
                activeBannerItems.length > 1 && "pb-16",
              )}
            >
              <div
                className={cn(
                  "relative w-full sm:rounded-lg",
                  isBlogPage
                    ? "h-95 min-h-95"
                    : " aspect-video sm:aspect-1012/378", // desktop specific
                )}
              >
                <div className={cn("relative w-full h-full sm:rounded-lg")}>
                  {/* Guarded. With BOTH background fields empty this still rendered: React drops an
                      empty src attribute, so the browser painted a broken image and showed the alt
                      text ("Banner background") across the banner. Visible on the live blog pages,
                      whose banner uses a background colour and no image at all. The tag also carried
                      fetchPriority="high" + loading="eager", competing for priority to fetch nothing.
                      Same guard shape as Banner10.tsx:496. */}
                  {(item.mobile_bg_image || item.desktop_bg_image) && (
                    <picture>
                      {item.desktop_bg_image && (
                        <source
                          srcSet={item.desktop_bg_image}
                          media={`(min-width: ${BANNER1_DESKTOP_MIN_PX}px)`}
                        />
                      )}

                      {item.mobile_bg_image && (
                        <source
                          srcSet={item.mobile_bg_image}
                          media={`(max-width: ${BANNER1_DESKTOP_MIN_PX - 1}px)`}
                        />
                      )}

                      <img
                        src={item.mobile_bg_image || item.desktop_bg_image}
                        alt={item.alt_image || "Banner background"}
                        className="object-cover sm:rounded-lg w-full h-full"
                        fetchPriority={index === 0 ? "high" : "auto"}
                        loading={index === 0 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    </picture>
                  )}

                  {/* Gradient Overlay */}
                  <div
                    className={cn(
                      "absolute inset-0 sm:rounded-lg z-1 flex sm:px-20 justify-center sm:justify-start h-full",
                      // Vertical placement. A marketing banner's mobile stack (pre-title,
                      // title, subtitle, CTAs) fills the height, so it is top-anchored by
                      // design. A blog banner carries only a title, which left it pinned
                      // 32px from the top with ~318px of dead space beneath — centre it
                      // instead. Desktop is unchanged: `items-center` covers what
                      // `sm:items-center` used to do. One align-items utility either way,
                      // so nothing depends on Tailwind's emission order.
                      isBlogPage ? "items-center" : "sm:items-center",
                      // Mobile vs desktop overlay gradient. `sm:` is 1080px here (see
                      // --breakpoint-sm in globals.css), the same boundary the <picture>
                      // sources above switch the background image at, so the gradient and
                      // the artwork it sits on always change together.
                      "[background-image:var(--banner1-overlay-mobile)] sm:[background-image:var(--banner1-overlay-desktop)]",
                      // Blog pages: horizontal gutter on mobile (desktop already uses sm:px-20).
                      isBlogPage && "max-sm:px-6",
                    )}
                    style={bannerOverlayGradients(item)}
                  >
                    {/* The 32px mobile top padding is what pinned the blog title to the
                        banner top; with items-center doing the work it would only push
                        the title back off-centre. Desktop keeps its sm:pt-16. */}
                    <div className={cn(isBlogPage ? "sm:pt-16" : "pt-8 sm:pt-16")}>
                      <div className={"flex flex-col gap-5 sm:gap-5"}>
                        <div className="flex flex-col gap-4 sm:gap-2 items-center sm:items-start">
                          {item?.pre_title && (
                            <p
                              className={cn(
                                "px-4 py-1 clip-path-inward-arrows text-xs lg:py-1.25 font-semibold text-center sm:py-2 sm:px-6 sm:text-heading-8-semibold w-fit",
                                "sm:py-[2px] ",
                                "md:text-body-1-semibold md:py-1",
                                "lg:text-label-1-semibold lg:py-1.25 ",
                                "xl:text-heading-5-semibold xl:py-0",
                                "type-h5",
                              )}
                              style={{
                                backgroundColor:
                                  item.university_tag_background_color ||
                                  "#BE2330",
                                color:
                                  item.university_tag_text_color || "#FFFFFF",
                              }}
                            >
                              {item.pre_title}
                            </p>
                          )}

                          <h1 className="flex flex-col items-center sm:items-start">
                            {item?.title_1 && (
                              <span
                                className={cn(
                                  "block text-heading-6-regular sm:text-new-heading-34-regular md:text-new-heading-42-regular lg:text-heading-2-medium xl:text-heading-2-regular",
                                  !isBlogPage &&
                                    "line-clamp-1 sm:line-clamp-none",
                                  // Blog pages: full title on mobile (no clamp) + centered on mobile, left-aligned on desktop.
                                  isBlogPage &&
                                    "line-clamp-none text-center sm:text-left",
                                  isBlogPage &&
                                    BLOG_BANNER_TITLE_MAX_WIDTH_CLASS,
                                )}
                                style={{
                                  color: item?.heading_text_color || "#FFFFFF",
                                }}
                              >
                                {item?.title_1}
                              </span>
                            )}
                            {item?.title_2 && (
                              <span
                                className={cn(
                                  "text-heading-6-bold block text-center sm:text-left",
                                  // Blog pages: no clamp on mobile; keep the 1-line clamp from sm up. Non-blog stays clamped at all sizes.
                                  isBlogPage ? "sm:line-clamp-1" : "line-clamp-1",
                                  "sm:text-new-heading-34-bold",
                                  "md:text-new-heading-42-bold",
                                  "lg:text-heading-2-bold",
                                  "xl:text-heading-2-bold",
                                  "type-h3",
                                )}
                                style={{
                                  color: item?.heading_text_color || "#FFFFFF",
                                }}
                              >
                                {item?.title_2}
                              </span>
                            )}
                          </h1>
                        </div>
                        {item?.button_text && (
                          <Button
                            onClick={() =>
                              dispatch(
                                handleOpenLeaddrop({
                                  variant: LEAD_DROP_VARIANTS.REGISTER,
                                }),
                              )
                            }
                            // arrowVariant="primary"
                            // withArrow
                            className={cn(
                              "mx-auto sm:mx-0 flex w-fit items-center justify-center px-5 gap-[6px] xs:text-heading-8-semibold sm:gap-2 sm:w-fit cursor-pointer sm:shadow-[0px_3.48px_2.53px_0px_#ca969b57]",
                              "sm:text-heading-9-semibold sm:py-[6px] sm:gap-[10px] sm:px-[14px] ",
                              "md:text-body-1-semibold md:px-4 ",
                              "lg:text-semibold lg:text-[20px] lg:py-2 lg:px-6 lg:gap-[10px] ",
                              "xl:text-heading-7-semibold",
                              "type-h6",
                            )}
                            style={{
                              backgroundColor: item?.button_color || "#BE2330",
                              color: item?.button_text_color || "#FFFFFF",
                            }}
                          >
                            {item?.button_text?.trim()}

                            {item?.button_icon && (
                              <div
                                className={cn(
                                  "relative",
                                  "sm:size-spacing-26",
                                  "md:size-8",
                                  "lg:text-body-5-semibold lg:size-9",
                                  "xl:size-9",
                                )}
                              >
                                <Image
                                  src={item?.button_icon || ""}
                                  alt={item?.button_icon_alt || ""}
                                  fill
                                />
                              </div>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Carousel1>

      {learners_description && Boolean(learners_status) && (
        <div
          className={cn(
            "pt-3 px-3 bg-[#ffffff] z-10",
            "sm:absolute sm:bottom-[23] sm:right-[31] sm:w-[50%] sm:pt-3 sm:pl-3 sm:rounded-tl-lg",
          )}
        >
          <Learners
            learners_description_section_color={
              learners_description_section_color || "#FFFFFF"
            }
            description={learners_description}
            metadata={
              sortedLearnersMetadata.length > 0 ? sortedLearnersMetadata : []
            }
            descriptionColor={learners_description_color}
          />
        </div>
      )}
    </section>
  );
};

export default Banner1;
